import { parseUtcMs } from "@/utils/datetime";
import type { UploadFileDeclareItem, UploadFileTicket } from "@/api/datasetUpload";
import type { ScannedFile } from "./folderScan";

// signed PUT 티켓 저장소. lazy declare 배치와 만료 정책의 단일 소유자다.
//
// Owns:
// - 전체 사전 선언 금지 정책: 15MB/s 에서 대용량 잡은 뒤쪽 티켓이 TTL(기본 1h) 안에 만료된다.
//   그래서 사용 시점에 필요한 경로부터 배치 크기만큼만 declare 한다(재선언은 재발급이고, DB 에
//   기록하지 않으므로 안전하다).
// - 만료 마진 판정: 만료까지 남은 시간이 마진보다 작으면 폐기하고 다시 선언한다. TTL 은 상수가
//   아니라 티켓별 upload_url_expires_at 만 신뢰한다.
// - 동시 사용 직렬화: 여러 풀 워커가 같은 miss 를 만나도 declare 는 한 번에 하나만 보낸다.
//
// Does not own:
// - PUT 실행·재시도(putFile, putPool), 어떤 파일을 올릴지의 결정(UploadEngine).

export type TicketStoreConfig = {
  declareBatchSize: number;
  expiryMarginMs: number;
};

export type DeclareFilesFn = (
  files: readonly UploadFileDeclareItem[],
) => Promise<UploadFileTicket[]>;

export class UploadTicketStore {
  private readonly tickets = new Map<string, UploadFileTicket>();
  // 아직 declare 하지 않은 경로 큐(업로드 순서와 같다). miss 가 나면 여기서 다음 배치를 채운다.
  private queue: ScannedFile[] = [];
  private readonly sizes = new Map<string, number>();
  // 동시 miss 를 직렬화하는 in-flight declare. 완료 후 각 대기자가 캐시를 다시 확인한다.
  private inFlightDeclare: Promise<void> | null = null;

  constructor(
    private readonly declare: DeclareFilesFn,
    private readonly config: TicketStoreConfig,
  ) {}

  // 업로드 대상 전체를 선언 큐에 넣는다(선언은 lazy). 기존 상태는 버린다.
  prime(files: readonly ScannedFile[]): void {
    this.tickets.clear();
    this.sizes.clear();
    this.queue = [...files];
    for (const file of files) {
      this.sizes.set(file.relativePath, file.sizeBytes);
    }
  }

  // 경로의 유효 티켓을 반환한다. 캐시 miss 이거나 만료되었으면 그 경로를 앞에 둔 다음 배치를 declare 한다.
  async ticketFor(relativePath: string): Promise<UploadFileTicket> {
    for (;;) {
      const cached = this.tickets.get(relativePath);
      if (cached !== undefined && this.isFresh(cached)) {
        return cached;
      }
      if (cached !== undefined) {
        this.tickets.delete(relativePath);
      }
      if (this.inFlightDeclare !== null) {
        await this.inFlightDeclare;
        continue;
      }
      const declarePromise = this.declareBatchFor(relativePath);
      this.inFlightDeclare = declarePromise;
      try {
        await declarePromise;
      } finally {
        this.inFlightDeclare = null;
      }
      const issued = this.tickets.get(relativePath);
      if (issued === undefined) {
        // 서버가 경로를 다시 정규화해 다른 키로 반환한 경우. FE 사전 정규화가 서버보다 엄격하므로
        // 정상 흐름에서는 여기에 오지 않는다(방어 코드).
        throw new Error(`Declare response is missing a ticket for path: ${relativePath}`);
      }
      return issued;
    }
  }

  // PUT 401/403(만료 시그니처)이면 티켓을 폐기해 다음 ticketFor 가 다시 선언하게 한다.
  invalidate(relativePath: string): void {
    this.tickets.delete(relativePath);
  }

  private async declareBatchFor(relativePath: string): Promise<void> {
    const batch = this.takeBatch(relativePath);
    let issued: UploadFileTicket[];
    try {
      issued = await this.declare(batch.paths.map((path) => this.toDeclareItem(path)));
    } catch (error) {
      // 선언이 실패했다. 큐에서 꺼낸 파일을 큐 앞에 되돌린다. 되돌리지 않으면 그 경로는 티켓도 없고
      // 큐에도 없다. 호출부가 다시 요청할 때 그 경로를 하나씩 따로 선언하게 된다.
      this.queue = [...batch.takenFromQueue, ...this.queue];
      throw error;
    }
    for (const ticket of issued) {
      this.tickets.set(ticket.relative_path, ticket);
    }
  }

  // 요청 경로와 선언 큐 앞쪽으로 배치를 구성한다. 선언이 성공하면 꺼낸 파일은 큐에 다시 넣지 않는다
  // (만료 재선언은 miss 경로 단위로 다시 들어온다). 선언이 실패하면 호출부가 꺼낸 파일을 되돌린다.
  private takeBatch(relativePath: string): { paths: string[]; takenFromQueue: ScannedFile[] } {
    const paths: string[] = [relativePath];
    const takenFromQueue: ScannedFile[] = [];
    const remaining: ScannedFile[] = [];
    for (const item of this.queue) {
      if (item.relativePath === relativePath) {
        takenFromQueue.push(item);
        continue;
      }
      if (paths.length < this.config.declareBatchSize && !this.tickets.has(item.relativePath)) {
        paths.push(item.relativePath);
        takenFromQueue.push(item);
      } else {
        remaining.push(item);
      }
    }
    this.queue = remaining;
    return { paths, takenFromQueue };
  }

  private toDeclareItem(relativePath: string): UploadFileDeclareItem {
    // 알고 있는 크기를 그대로 보낸다. gt=0 계약에 맞추는 정규화(0바이트·크기 미상은 필드 생략)는
    // declareDatasetUploadFiles 가 wire 직전에 소유한다.
    return { relative_path: relativePath, size_bytes: this.sizes.get(relativePath) };
  }

  // 표시가 아니라 재서명 여부를 정하는 게이트라 공용 파서를 쓴다. zone 없는 문자열을 로컬 시각으로
  // 읽으면 만료가 뷰어 offset 만큼 과거로 이동해 모든 티켓이 stale 로 보인다. 그러면 파일마다
  // 재선언 요청을 보낸다(경고 없는 network N+1). 파싱할 수 없으면 보수적으로 만료로 본다.
  private isFresh(ticket: UploadFileTicket): boolean {
    const expiresAt = parseUtcMs(ticket.upload_url_expires_at);
    if (expiresAt === null) {
      return false;
    }
    return expiresAt - Date.now() > this.config.expiryMarginMs;
  }
}
